import { isWindows, isMac } from "./keyboard-shortcuts";

export type MouseType = "mouse" | "trackpad";

/**
 * Determines if a given WheelEvent is emitted by a mouse or a trackpad.
 * @returns "mouse" if the event is emitted by a mouse, "trackpad" if emitted by a trackpad.
 */
export function classifyHardware(event: WheelEvent, lastResult: { time: Date; hardware: MouseType } | null): MouseType {
  // @ts-expect-error Some parameters may be undefined due to deprecations. We deal with that below.
  const { wheelDelta, wheelDeltaY, deltaY, deltaX, deltaMode } = event;

  // Check the wheelDelta parameter if available. It is deprecated but works on the latest
  // versions of all browsers with no scheudled removal date as of writing this.
  // Detecting using wheelDelta is crucial since its replacement deltaMode is not yet indicative.
  if (wheelDelta !== undefined && wheelDelta !== null) {
    const magicDeltaOffset = Math.floor(Math.abs(deltaY) * 3) - Math.abs(wheelDeltaY);
    const useSequentialEventsMilliseconds = 100; // Upped fromm 50 since slow canvases had jumps while panning with trackpad

    // Sometimes (random chance) when scrolling a mouse fast the wheelDeltaY will be 3* the deltaY.
    // Sometimes (????) by panning the trackpad fast the deltaY*3 will 1 less than wheelDeltaY (Becuase, of course it would)
    // To counter this, if we made an assessment less than our threshold, we stick with it!
    if (lastResult && Date.now() - lastResult?.time.getTime() < useSequentialEventsMilliseconds) {
      return lastResult.hardware;
    }

    // If an event transmits non-zero values for both deltaX and deltaY, it's a trackpad.
    if (deltaX !== 0 && deltaY !== 0) {
      return "trackpad";
    }

    // On windows:
    // 1. If deltaY is non-zero, and floored abs(deltaY) === wheelDeltaY, it's a trackpad.
    if (isWindows() && deltaY !== 0) {
      const windowsMagicOffset = Math.abs(wheelDeltaY) - Math.floor(Math.abs(deltaY));
      if (windowsMagicOffset === 0 || windowsMagicOffset === -1 || windowsMagicOffset === 1) return "trackpad";
    }

    // On Safari:
    // 1. Detect if the numbers returned from the wheelevent are integers (because the delta detection does not work)
    // 2. if they are, it's a trackpad. otherwise it's a mouse
    // (For some reason pinching the trackpad on safari does NOT emit a mouse event but works somehow. not complaining.)

    // We can detect safai on desktop using window.safari. This is not available on mobile.
    // A mobile-friendly solution can be implemented but we currrently do not support mobile.
    // @ts-expect-error Window.safari will be defined on safari, this is safe.
    const isSafari = window.safari !== undefined;
    if (isSafari) {
      // Safari always transmits integers on trackpad and flats on mouse.
      if (Number.isInteger(deltaY) && Number.isInteger(deltaX)) {
        return "trackpad";
      }
      return "mouse";
    }

    // Everywhere else:
    // 1. Handle known mouse case
    // 2. Detect trackpad/mouse using deltaY and wheelDeltaY

    // 1. Known mouse case: wheelDeltaY is offset less that 0 or more than 1 from 3* deltaY
    if (magicDeltaOffset !== 0 && magicDeltaOffset !== 1) {
      return "mouse";
    }

    // 2. Edge cases
    if (magicDeltaOffset === 0) {
      // On firefox both the delta and integer rules don't work.
      // Currently we just always assume it's a trackpad.
      return "trackpad";
    }
    return "mouse"; // Pressing SHIFT on a mouse will cause it to behvae like a trackpad, but we can't detect that reliably.
  } else {
    // In this case, we are on a futue browser (Hello from the past!)
    // Mouse wheels should be configured to Line (or page?) delta mode, while trackpad should use Pixel delta mode.
    if (deltaMode === WheelEvent.DOM_DELTA_LINE || deltaMode === WheelEvent.DOM_DELTA_PAGE) {
      return "mouse";
    } else {
      return "trackpad";
    }
  }
}

/**
 * Calculates whether the user is on mouse or trackpad in a legacy manner.
 * This assumes trackpads always transmit integer deltas and mice always transmit floating point deltas.
 * Known issues:
 *  - On any browser, zooming in or out on a trackpad will cause the deltas to be floats and behave like a mouse.
 *  - This basically only works on chrome with 100% zoom. Other browsers break it.
 * @returns an integer representing the zoom behavior, and the deltas.
 */
export function legacyZoomBehavior(event: WheelEvent) {
  const { deltaX, deltaY, ctrlKey, metaKey, shiftKey, deltaMode } = event;
  const ctrlOrCmd = isMac() ? +metaKey : +ctrlKey;

  let dx = deltaX;
  let dy = deltaY;

  if (dx === 0 && shiftKey) {
    [dx, dy] = [dy, dx]; // swap deltas
  } else {
    if (deltaMode === WheelEvent.DOM_DELTA_LINE) {
      dy = dy * 8;
    } else if (deltaMode === WheelEvent.DOM_DELTA_PAGE) {
      dy = dy * 24;
    }
    dy = Math.sign(dy) * Math.min(24, Math.abs(dy));
  }

  let zoomBehavior = 1 + +(deltaY === 0 ? Number.isInteger(deltaX) : Number.isInteger(deltaY)) + ctrlOrCmd * 2;
  if (deltaX && !Number.isInteger(deltaX)) {
    zoomBehavior -= 1;
  }

  if (zoomBehavior === 2) {
    // Undo dx,dy edita at this stage
    dx = deltaX;
    dy = deltaY;
  }

  return { dx, dy, zoomBehavior };
}
